import React, { useContext } from 'react';
import { makeStyles } from '@mui/styles';
import { Box, Grid, Paper, Typography, useMediaQuery, useTheme } from '@mui/material';
import { PatientTabsContext } from '../../../lib/contexts';
import useMetric from '../../../lib/hooks/useMetric';
import { AdminProgressBar } from '../../../components/AdminProgressBar';
import { ArrowForward } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import PatientNutrientSummaryBox from './PatientNutrientSummaryBox';
import PatientNutrientProfileBox from './PatientNutrientProfileBox';
import usePatientNutrientProfile from '../../../lib/hooks/usePatientNutrients';
import PatientActionBox from './PatientActionBox';
import PatientTipBox from './PatientTipBox';

const useStyles = makeStyles((theme) => ({
  patientChartCard: {
    ...theme.globalClasses.patientChartCard,
    ...theme.globalClasses.adminTableWrapper,
    margin: 0,
    padding: 0,
    marginTop: 16,
  },
  topBar: {
    marginBottom: 16,
  },
  icon: theme.globalClasses.adminTableIcon,
  tableHead: theme.globalClasses.adminTableHead,
  tableRow: theme.globalClasses.adminTableRow,
  graphContainer: {
    // Add any existing styles for the graph container
  },
  '@media print': {
    graphContainer: {
      overflow: 'visible',
      transform: 'scale(0.50)',
      transformOrigin: 'top left',
      width: '100%',
      height: 'auto',
      position: 'relative',
    },
  },
  link: theme.globalClasses.link,
  boxContainer: {
    marginBottom: theme.spacing(3),
  },
}));

const PatientKeyNutrientsBox = ({ summary = false, setSetSubtab }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    state: { currentTab },
  } = useContext(PatientTabsContext);
  const { isLoading, data } = useMetric('priorities', currentTab.id);

  // Get workflow token from URL
  const workflowShareUrl = currentTab?.workflowShareUrl;
  const workflowToken = workflowShareUrl ? workflowShareUrl.split('#/s/')[1] : null;

  const { isLoading: nutrientsDataLoading, data: nutrientsData } = usePatientNutrientProfile(workflowToken);
  const { isLoading: checkinHistoryLoading, data: checkinHistoryData } = useMetric('checkin_history', currentTab.id);

  const topBarMargin = summary ? '12px' : '16px';

  // rules for display - if there are food changes to increase, we display what we can
  // otherwise don't display on summary and show message on key nutrients tab
  const hasData = !isLoading && data?.metric?.value?.food_changes?.increases?.length > 0;

  const HeaderComponent = () => (
    <Grid
      container
      item
      xs={12}
      justifyContent="space-between"
      alignItems="center"
      sx={{
        width: '100%',
        maxWidth: '100%',
        mx: 0,
        padding: 0,
        marginBottom: topBarMargin,
      }}
    >
      {summary ? (
        <>
          {hasData && (
            <>
              <Typography
                variant={'overline'}
                sx={{ paddingBottom: 0 }}
              >
                Key Nutrients
              </Typography>
              <Typography
                variant={'tableHeadLink'}
                onClick={() => setSetSubtab('1')}
                sx={{ display: 'flex', alignItems: 'center', paddingBottom: 0 }}
              >
                View Full Breakdown
                <IconButton sx={{ paddingLeft: '8px' }}>
                  <ArrowForward className={classes.icon} />
                </IconButton>
              </Typography>
            </>
          )}
        </>
      ) : (
        <Typography variant={'h2'}>Key Nutrient Breakdown</Typography>
      )}
    </Grid>
  );

  const NutrientProfileComponent = ({ maxWidth = '100%' }) =>
    hasData &&
    nutrientsData.length > 0 && (
      <PatientNutrientProfileBox
        data={nutrientsData}
        maxWidth={maxWidth}
      />
    );

  const IncreasesComponent = () =>
    hasData && (
      <PatientNutrientSummaryBox
        data={data.metric.value?.food_changes?.increases}
        isIncrease={true}
        heading={'Increase These'}
        tip={'Start introducing more of these in your diet to improve your condition.'}
      />
    );

  const DecreasesComponent = () =>
    hasData &&
    data.metric.value?.food_changes?.decreases?.length > 0 && (
      <PatientNutrientSummaryBox
        data={data.metric.value?.food_changes?.decreases}
        isIncrease={false}
        heading={'Decrease These'}
        tip={'Decrease these foods to improve your condition.'}
      />
    );

  const ActionBoxComponent = ({ width = '336px' }) =>
    summary &&
    data.metric.value?.this_week_action && (
      <Box sx={width ? { width } : {}}>
        <PatientActionBox
          data={{
            description: data.metric.value?.this_week_action_description,
            action: data.metric.value?.this_week_action,
            checkins: checkinHistoryData?.metric?.value?.length,
          }}
        />
      </Box>
    );

  const TipBoxComponent = ({ width = '336px' }) =>
    summary &&
    data.metric.value?.todays_tip && (
      <Box sx={width ? { width } : {}}>
        <PatientTipBox
          data={{
            tip: data.metric.value?.todays_tip_description,
            description: data.metric.value?.todays_tip,
            resources: '',
          }}
        />
      </Box>
    );

  const MobileView = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '24px',
        width: '100%',
      }}
    >
      <NutrientProfileComponent maxWidth={336} />
      <IncreasesComponent />
      <DecreasesComponent />
      <ActionBoxComponent />
      <TipBoxComponent />
    </Box>
  );

  const DesktopView = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: '24px',
        width: '100%',
        padding: 0,
        justifyContent: 'flex-start',
      }}
    >
      {hasData ? (
        <>
          {/* With data - show the two column layout */}
          {/* First column - Nutrient Profile */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', width: '336px' }}>
            <NutrientProfileComponent />
          </Box>

          {/* Second column - Increases/Decreases and Action/Tip boxes */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', flex: 1 }}>
            {/* Top row: Increases and Decreases side by side */}
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: '24px', flexWrap: 'wrap' }}>
              <IncreasesComponent />
              <DecreasesComponent />
            </Box>

            {/* Bottom row: Action and Tip boxes side by side when relevant */}
            {summary && (
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: '24px', flexWrap: 'wrap' }}>
                <ActionBoxComponent />
                <TipBoxComponent />
              </Box>
            )}
          </Box>
        </>
      ) : (
        /* No data - just show action and tip boxes in a single row flush left */
        summary && (
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: '24px' }}>
            <ActionBoxComponent />
            <TipBoxComponent />
          </Box>
        )
      )}
    </Box>
  );

  return (
    <Paper className={classes.patientChartCard}>
      <Grid
        container
        spacing={3}
        className={classes.topBar}
      >
        <Grid
          item
          xs={12}
          className={`patient-key-nutrients-box`}
          id="patient-key-nutrients-box"
        >
          <HeaderComponent />

          {isLoading || nutrientsDataLoading || checkinHistoryLoading ? (
            <AdminProgressBar />
          ) : !hasData && !summary ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '200px',
                width: '100%',
              }}
            >
              <Typography variant="body1">No assessment data available.</Typography>
            </Box>
          ) : (
            <Box sx={{ width: '100%' }}>{isMobile ? <MobileView /> : <DesktopView />}</Box>
          )}
        </Grid>
      </Grid>
    </Paper>
  );
};

export default PatientKeyNutrientsBox;
